import _ from "lodash";
import client from "lib/apolloClient/client";
import { cache } from "lib/apolloClient";
import ACLStore from "lib/aclStore";
import {
  getCategoryWiseAssessmentsQuery,
  getStudentWiseRatingsQuery,
  getCourseAcademicYearTermsQuery,
  getCourseGradesQuery,
  getOrganizationAssignmentsGradebookQuery,
  getGradebookAssignmentsQuery,
  getStudentIbGradeRatingsQuery,
  getOrganizationCoursesQuery,
  getStudentAssignmentsQuery,
  getAcademicCriteriaSetsQuery,
  getCourseUserMapQuery,
  getOrganizationPointSetQuery,
  getEvaluatedButNotSharedAssignmentsCountByTermQuery,
  getStudentsElementRatingsQuery,
  getStudentElementRemarksQuery,
} from "./GradebookQueries";

import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_UBD,
  ASSESSMENT_TYPE_QUICK_TASK,
  LEARNING_ENGAGEMENT,
  ASSESSMENT,
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  ARCHIVED,
  PUBLISHED,
  GROUP_TYPE_WORKSHEET,
  ASC,
  SCORE,
  AI_TUTOR,
  COURSE_BASED_GRADING,
} from "Constants/stringConstants";
import { EXCLUDED_ASSESSMENT_TYPES } from "store/static";

import { getIsScoreV2 } from "routes/routes/Administrator/routes/DPAcademicSetup/routes/AssessmentSetup/modules/utils";
import { ASSESSMENT_TYPE_WORKSHEET } from "Worksheet/modules/WorksheetConstants";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getAssesmentTypeOptions } from "ProgressSummary/modules/Utils";
import { getIsSubjectGradebookEnabled } from "GradingSetupV2/modules/Utils";
import {
  academicCriteriaSetFragment,
  academicCriteriaValueFragment,
  assignedStudentFragment,
  categoriesItemFragment,
  studentRatingsFragment,
} from "./GradebookFragments";
import { CATEGORY, DISPLAY_SEQUENCE } from "./Constants/stringConstants";
import {
  getAssignmentOrderBy,
  getOthersDisplaySettings,
} from "AssessmentsGradebookV2/components/ConfigureViews/components/DisplaySettings/DisplaySettingsUtils";
import {
  MANAGE_GRADEBOOK_PERMISSION,
  USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
} from "Constants/permissionConstants";
import { getIsConfigurationEnabledForCPAY } from "modules/PermissionModule";
import { getAcademicSetupTypeIfLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import useFetchQuery from "Hooks/useFetchQuery";

export const configureColumnOptionQueryVariableMap = {
  CATEGORY_TOTAL: "SCORE",
  CATEGORY_GRADE: "IB_GRADE",
  FINAL_GRADE: "IB_GRADE",
  FINAL_SCORE: "SCORE",
  LOCAL_GRADE: "LOCAL_GRADE",
  CRITERION_A: "MYP_CRITERIA_A",
  CRITERION_B: "MYP_CRITERIA_B",
  CRITERION_C: "MYP_CRITERIA_C",
  CRITERION_D: "MYP_CRITERIA_D",
  CRITERIA_TOTAL: "MYP_CRITERIA_TOTAL",
};

export const getColumnTypeFilters = ({
  isNewGradingSetupEnabled,
  selectedColumns,
  columnKey,
}) => {
  const columnValues = _.get(selectedColumns, columnKey);

  // undefined - to not apply any filter and get all columns of that type
  // [] - to explicitly mention that no column of that type is selected and avoid showing all columns of that type
  if (isNewGradingSetupEnabled) {
    return !_.isEmpty(columnValues) ? undefined : [];
  }

  return _.compact(
    _.map(columnValues, item => configureColumnOptionQueryVariableMap[item])
  );
};

export const getStudentWiseRatingsVariables = _.memoize(
  ({
    courseId,
    isCourseBasedGrading,
    isUsingNewGradingSetupInCourses,
    learningCourseId,
    selectedSubject,
    subjectIds,
    selectedGradingPeriod,
    gradeId,
    curriculumProgramId,
    isCumulativePeriod,
    showSameSubjectGradeTasksInGradebook,
    academicYearId,
  }) => {
    return {
      id: courseId,
      courseUserMapFilters: {
        archivalState: "ALL",
        userTypes: ["student"],
        isFetchRemovedUsers: true,
      },
      filtersForGeneric: {
        curriculumProgramId,
        learningCourseIds: isCourseBasedGrading ? learningCourseId : undefined,
        courseIds:
          !isUsingNewGradingSetupInCourses &&
          !showSameSubjectGradeTasksInGradebook
            ? courseId
            : undefined,
        subjectIds: !_.isEmpty(selectedSubject) ? subjectIds : undefined,
        academicTermId:
          isCumulativePeriod && !_.isEmpty(selectedGradingPeriod)
            ? selectedGradingPeriod
            : undefined,
        academicTermIds: !_.isEmpty(selectedGradingPeriod)
          ? selectedGradingPeriod
          : undefined,
        gradeIds: !_.isEmpty(gradeId) ? gradeId : undefined,
        academicYearId,
      },
    };
  },
  JSON.stringify
);

export const getOrganizationAssignmentsGradebookFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getOrganizationAssignmentsGradebookQuery,
      variables,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getCourseGradesFromCache = ({ id, courseGradeFilters }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getCourseGradesQuery,
      variables: {
        id,
        courseGradeFilters,
      },
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getCategoryWiseAssessmentsFromCache = ({ variables }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getCategoryWiseAssessmentsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return queryData;
  else return {};
};

export const getStudentWiseRatingsFromCache = ({ variables }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getStudentWiseRatingsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return queryData;
  else return {};
};

export const getCourseAcademicYearTermsQueryFromCache = ({
  id,
  curriculumProgramIds,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getCourseAcademicYearTermsQuery,
      variables: {
        id,
        curriculumProgramIds,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getGroupOrAssessmentTypes = _.memoize(
  ({ curriculumType, selectedAssessmentTypes }) => {
    switch (curriculumType) {
      case CURRICULUM_TYPE_UBD: {
        const groupTypes = _.map(selectedAssessmentTypes, type => {
          switch (type) {
            case "qt":
            case ASSESSMENT_TYPE_QUICK_TASK:
              return ASSESSMENT_TYPE_QUICK_TASK;
            case "le":
            case LEARNING_ENGAGEMENT:
              return LEARNING_ENGAGEMENT;
            case ASSESSMENT_TYPE_WORKSHEET:
              return GROUP_TYPE_WORKSHEET;
            case "ai_tutor":
              return AI_TUTOR;
            default:
              return ASSESSMENT;
          }
        });
        return {
          assessmentTypes: undefined,
          groupTypes,
        };
      }

      default: {
        return {
          assessmentTypes: selectedAssessmentTypes,
          groupTypes: undefined,
        };
      }
    }
  },
  params => JSON.stringify(params)
);

const getEvaluationTypeBasedAssessmentToolTypeFilter = _.memoize(
  ({ selectedAssessmentTools = [] }) => {
    if (!_.isEmpty(selectedAssessmentTools)) {
      const tools = [];

      _.forEach(selectedAssessmentTools, tool => {
        if (_.includes(["MYP_BASED_RUBRIC", "CRITERIA"], tool)) {
          tools.push(
            "MYP_OBJECTIVE_RUBRIC",
            "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC"
          );
        } else if (_.includes([ASSESSMENT_GRADE_BOUNDARY], tool)) {
          tools.push(
            ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
            ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
            ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
            ASSESSMENT_GRADE_BOUNDARY
          );
        } else {
          tools.push(tool);
        }
      });

      return _.uniq(tools);
    }

    return [];
  },
  params => JSON.stringify(params)
);

export const getCoursePrimaryGradeIdFromCache = ({
  courseId,
  gradebookSelectedTermAcademicYearId,
  selectedAcademicYearId,
}) => {
  const gradesData = getCourseGradesFromCacheV2({
    courseId,
    gradebookSelectedTermAcademicYearId,
    selectedAcademicYearId,
  });
  const grades = _.get(gradesData, "grades");
  const gradeId = _.get(gradesData, "primaryGrade.id") || _.get(grades, "0.id");

  return gradeId;
};

export const getGradebookAllAssessmentsVariables = params => {
  const {
    courseId,
    filters = {},
    organizationId,
    curriculumType,
    primaryGradeId,
    showSameSubjectGradeTasksInGradebook,
    isHideTasksFromOtherClasses,
    isHideTasksExcludedFromTermTotal,
  } = params;
  // NOTE: This function is used to generate variables for queries in GradebookTable.js
  // And this function is also used to get variables to refetch queries after creating/editing/deleting a task in ClassRooomModule.js
  // If any of the parameters are changed here, please make sure to update the function call in both the above mentioned places
  // to ensure that the same query variables are generated from both places

  const selectedSubject = _.get(filters, "selectedSubject", "");
  const evaluationTypes = _.get(filters, "evaluationTypes", "ALL");
  const selectedAssessmentTools = _.get(filters, "selectedAssessmentTools", []);
  const selectedAssessmentTypes = _.get(filters, "selectedAssessmentTypes", []);
  const selectedTerms = _.get(filters, "selectedTerms", []);
  const searchText = _.get(filters, "searchText", "");
  const selectedCategories = _.get(filters, "selectedCategories", []);
  const selectedStudents = _.sortBy(_.get(filters, "selectedStudents", []));

  const isAssessmentScoringEnabled =
    evaluationTypes === "ALL" || evaluationTypes === "score";

  const { assessmentTypes = [], groupTypes = [] } = getGroupOrAssessmentTypes({
    curriculumType,
    selectedAssessmentTypes,
  });

  const assessmentTypeOptions = getAssesmentTypeOptions({
    t: _.noop,
    evaluationTypes: evaluationTypes,
    curriculumType: curriculumType,
  });

  // Sending no filters if all assessmentTypes are selected
  const _assessmentTypes =
    _.size(assessmentTypeOptions) === _.size(assessmentTypes)
      ? []
      : assessmentTypes;

  const showTasksFromOtherClasses =
    !isHideTasksFromOtherClasses && showSameSubjectGradeTasksInGradebook;

  return {
    organizationId,
    courseIds: showTasksFromOtherClasses ? undefined : courseId,
    primaryGradeId: showTasksFromOtherClasses ? primaryGradeId : undefined,
    contentTypes: "ASSESSMENT",
    isTeacherEvaluationEnabled: true,
    assessmentTypes: _assessmentTypes,
    assessmentToolTypes: getEvaluationTypeBasedAssessmentToolTypeFilter({
      selectedAssessmentTools,
    }),
    isAssessmentScoringEnabled,
    searchText,
    first: 10,
    academicTermIds: !_.isEmpty(selectedTerms) ? selectedTerms : [],
    categoryIds: selectedCategories,
    subjectIds: !_.isEmpty(selectedSubject)
      ? _.split(selectedSubject, ",")
      : [],
    excludedAssessmentTypes:
      curriculumType === CURRICULUM_TYPE_DP ? EXCLUDED_ASSESSMENT_TYPES : [],
    groupTypes,
    states: [ARCHIVED, PUBLISHED],
    includedStudentIds: showTasksFromOtherClasses
      ? selectedStudents
      : undefined,
    courseMapFilters: {
      courseIds: courseId,
    },
    isExcludedFromFinalGradeCalculation: isHideTasksExcludedFromTermTotal
      ? false
      : undefined,
  };
};

export const getGradebookCategoryWiseAssessmentsVariables = params => {
  const {
    courseId,
    filters = {},
    organizationId,
    curriculumProgramId,
    curriculumType,
    selectedAcademicYearId,
    primaryGradeId,
    showSameSubjectGradeTasksInGradebook,
    isHideTasksFromOtherClasses,
    isHideTasksExcludedFromTermTotal,
  } = params;
  // NOTE: This function is used to generate variables for queries in GradebookTable.js
  // And this function is also used to get variables to refetch queries after creating/editing/deleting a task in ClassRooomModule.js
  // If any of the parameters are changed here, please make sure to update the function call in both the above mentioned places
  // to ensure that the same query variables are generated from both places

  const selectedSubject = _.get(filters, "selectedSubject", "");
  const evaluationTypes = _.get(filters, "evaluationTypes", "ALL");
  const selectedAssessmentTools = _.get(filters, "selectedAssessmentTools", []);
  const selectedAssessmentTypes = _.get(filters, "selectedAssessmentTypes", []);
  const selectedTerms = _.get(filters, "selectedTerms", []);
  const searchText = _.get(filters, "searchText", "");
  const selectedCategories = _.get(filters, "selectedCategories", []);
  const selectedStudents = _.sortBy(_.get(filters, "selectedStudents", []));

  const gradesData = getCourseGradesFromCache({
    id: courseId,
    courseGradeFilters: {
      academicYearId: selectedAcademicYearId,
    },
  });
  const isScoreV2 = getIsScoreV2({ curriculumType });

  const grades = _.get(gradesData, "grades", []);
  const gradeIds =
    _.get(gradesData, "primaryGrade.id") || _.get(grades, "0.id");

  const associatedEntities = [];

  if (isScoreV2 && !_.isEmpty(selectedTerms)) {
    associatedEntities.push({
      ids: selectedTerms,
      type: "ACADEMIC_TERM",
    });
  }
  if (!_.isEmpty(selectedSubject)) {
    associatedEntities.push({ ids: selectedSubject, type: "SUBJECT" });
  }
  if (!_.isEmpty(gradeIds)) {
    associatedEntities.push({ ids: gradeIds, type: "GRADE" });
  }

  const { assessmentTypes = [], groupTypes = [] } = getGroupOrAssessmentTypes({
    curriculumType,
    selectedAssessmentTypes,
  });

  const assessmentTypeOptions = getAssesmentTypeOptions({
    t: _.noop,
    evaluationTypes: evaluationTypes,
    curriculumType: curriculumType,
  });

  // Sending no filters if all assessmentTypes are selected
  const _assessmentTypes =
    _.size(assessmentTypeOptions) === _.size(assessmentTypes)
      ? []
      : assessmentTypes;

  const assessmentToolTypes = getEvaluationTypeBasedAssessmentToolTypeFilter({
    selectedAssessmentTools,
  });

  const showTasksFromOtherClasses =
    !isHideTasksFromOtherClasses && showSameSubjectGradeTasksInGradebook;

  return {
    id: organizationId,
    courseIds: courseId,
    assessmentToolTypes,
    academicCriteriaSetsFilter: {
      curriculumProgramIds: curriculumProgramId,
      criteriaType: "ASSESSMENT_CATEGORY",
      associatedEntities,
    },
    assignmentFilters: {
      curriculumProgramIds: curriculumProgramId,
      courseIds: showTasksFromOtherClasses ? undefined : courseId,
      primaryGradeId: showTasksFromOtherClasses ? primaryGradeId : undefined,
      contentTypes: "ASSESSMENT",
      searchText,
      academicTermIds: !_.isEmpty(selectedTerms) ? selectedTerms : undefined,
      subjectIds: !_.isEmpty(selectedSubject)
        ? _.split(selectedSubject, ",")
        : undefined,
      isAssessmentScoringEnabled: true,
      assessmentTypes: _assessmentTypes,
      groupTypes,
      states: [ARCHIVED, PUBLISHED],
      assessmentToolTypes,
      categoryIds: !_.isEmpty(selectedCategories)
        ? selectedCategories
        : undefined,
      includedStudentIds: showTasksFromOtherClasses
        ? selectedStudents
        : undefined,
      isExcludedFromFinalGradeCalculation: isHideTasksExcludedFromTermTotal
        ? false
        : undefined,
    },
    courseMapFilters: {
      courseIds: courseId,
    },
  };
};

export const getDownloadGradebookInputVariables = ({
  gradingPeriodList,
  academicTermIds,
  assessmentToolTypes,
  curriculumProgramId,
  dataSet,
  elementTypes,
  subjectIds,
  courseIds,
  type,
  isAdminGradebook,
  exportType,
  studentWiseFilters,
  selectedColumns,
  academicYearId,
  isStandardsGradebookSelected,
}) => {
  switch (type) {
    case "ASSESSMENT_GRADEBOOK": {
      return [
        {
          summaryType: type,
          courseIds: isAdminGradebook ? courseIds : undefined,
          curriculumProgramId: isAdminGradebook
            ? curriculumProgramId
            : undefined,
          assessmentFilters: {
            academicTermIds,
            assessmentToolTypes,
            subjectIds,
          },
          exportType: isAdminGradebook ? exportType : undefined,
          studentWiseFilters: isAdminGradebook ? studentWiseFilters : undefined,
          academicYearId,
        },
      ];
    }
    case "GRADEBOOK_SUMMARY": {
      const filteredGradingPeriods = _.filter(gradingPeriodList, ({ value }) =>
        _.includes(academicTermIds, value)
      );

      const isLearningGoalsSelected =
        !_.isEmpty(filteredGradingPeriods) && !_.isEmpty(elementTypes);

      const learningGoalsInput = isLearningGoalsSelected
        ? [
            {
              summaryType: "LEARNING_GOALS",
              learningGoalsFilters: {
                academicTermIds: _.map(filteredGradingPeriods, "value"),
                elementTypes,
                dataSet,
              },
            },
          ]
        : [];

      const standardsInput = isStandardsGradebookSelected
        ? [
            {
              summaryType: "STANDARDS_GRADEBOOK",
              filters: {
                academicTermIds,
              },
            },
          ]
        : [];

      const isNewGradingSetupEnabledValue = getIsConfigurationEnabledForCPAY({
        configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
      });

      return [
        {
          summaryType: "ASSESSMENT_GRADEBOOK",
          assessmentFilters: {
            academicTermIds,
            assessmentToolTypes,
            subjectIds,
            categoryColumnTypes: getColumnTypeFilters({
              isNewGradingSetupEnabled: isNewGradingSetupEnabledValue,
              selectedColumns,
              columnKey: "categoryColumnTypes",
            }),
            gradingPeriodColumnsTypes: getColumnTypeFilters({
              isNewGradingSetupEnabled: isNewGradingSetupEnabledValue,
              selectedColumns,
              columnKey: "gradingPeriodColumnsTypes",
            }),
          },
          academicYearId,
        },
        ...learningGoalsInput,
        ...standardsInput,
      ];
    }
    case "LEARNING_GOALS_GRADEBOOK": {
      return [
        {
          summaryType: "LEARNING_GOALS",
          learningGoalsFilters: {
            academicTermIds,
            elementTypes,
            dataSet,
          },
        },
      ];
    }
    case "PROGRESS_REPORT_OVERVIEW_GRADEBOOK": {
      return [
        {
          summaryType: "MYP_PROGRESS_REPORT_OVERVIEW",
        },
      ];
    }
    default:
      break;
  }
};

/**
 * Retrieves variables for student-wise ratings query in Gradebook.
 *
 * @param {Object} options - An object containing options for fetching gradebook data.
 * @param {string} options.courseId - The ID of the course for which to fetch gradebook data.
 * @param {Object} options.filters - Filters from redux that were applied in Gradebook.
 * @param {string} options.curriculumProgramId - The ID of the curriculum program.
 * @param {string} options.academicYearId - The academic year ID.
 * @param {string} options.selectedAcademicYearId - The ID of the academic year of the currently selected term in Gradebook.
 * @param {boolean} options.showSameSubjectGradeTasksInGradebook - Whether to show same subject grade tasks in Gradebook.
 *
 * @returns {Object} An object containing the variables for the query.
 */
export const getGradebookStudentWiseRatingsVariables = ({
  courseId,
  filters,
  curriculumProgramId,
  academicYearId,
  selectedAcademicYearId,
  showSameSubjectGradeTasksInGradebook,
  isCourseBasedGrading,
  isUsingNewGradingSetupInCourses,
  learningCourseId,
}) => {
  const { selectedSubject, selectedTerms: selectedGradingPeriod } = filters;

  const subjectIds = _.split(selectedSubject, ",");

  const gradesData = getCourseGradesFromCacheV2({
    courseId,
    gradebookSelectedTermAcademicYearId: selectedAcademicYearId,
    selectedAcademicYearId: academicYearId,
  });

  const gradeId =
    _.get(gradesData, "primaryGrade.id", "") ||
    _.get(_.first(gradesData?.grades), "id", "");

  return getStudentWiseRatingsVariables({
    courseId,
    isCourseBasedGrading,
    isUsingNewGradingSetupInCourses,
    learningCourseId,
    selectedSubject,
    subjectIds,
    selectedGradingPeriod,
    gradeId,
    curriculumProgramId,
    isCumulativePeriod: false, // can't create assignment for cumulative
    showSameSubjectGradeTasksInGradebook,
    academicYearId,
  });
};

export const getStudentIbGradeRatingsFromCache = ({
  id,
  filtersForRatingHistory,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getStudentIbGradeRatingsQuery,
      variables: {
        id,
        filtersForRatingHistory,
      },
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return queryData;
  else return {};
};

export const getOrganizationCoursesFromCache = ({
  organizationId,
  courseFilters,
}) => {
  try {
    const queryData = client.readQuery({
      query: getOrganizationCoursesQuery,
      variables: {
        organizationId,
        courseFilters,
      },
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getStudentAssignmentsFromCache = ({ id, filters }) => {
  try {
    const queryData = client.readQuery({
      query: getStudentAssignmentsQuery,
      variables: {
        id,
        filters,
      },
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getAcademicCriteriaSetsFromCache = ({ id, filters }) => {
  try {
    const queryData = client.readQuery({
      query: getAcademicCriteriaSetsQuery,
      variables: {
        id,
        filters,
      },
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const writeStudentWiseRatingsToCache = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getStudentWiseRatingsQuery,
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
};

export const getCourseUserMapFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getCourseUserMapQuery,
      variables,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getAssignedStudentFromCache = ({ studentAssignmentId }) => {
  let fragmentData = {};
  try {
    fragmentData = client.readFragment({
      id: `StudentAssignment:${studentAssignmentId}`,
      fragment: assignedStudentFragment.assignedStudent,
      fragmentName: "assignedStudentItem",
    });
  } catch (e) {
    return {};
  }
  return fragmentData;
};

export const getGradebookAssignmentsVariables = params => {
  const {
    courseId,
    filters = {},
    organizationId,
    curriculumType,
    primaryGradeId,
    showSameSubjectGradeTasksInGradebook,
    isHideTasksFromOtherClasses,
    isHideTasksExcludedFromTermTotal,
    shouldGroupByCategories = false,
    displaySettings,
    isCumulativePeriod,
    associatedTerms,
    cumulativeTotalCalculationMethod,
    isAssessmentGradebookHeaderEnhancements,
    learningCourseId,
    curriculumProgramId,
  } = params;
  // NOTE: This function is used to generate variables for queries in GradebookTable.js
  // And this function is also used to get variables to refetch queries after creating/editing/deleting a task in ClassRooomModule.js
  // If any of the parameters are changed here, please make sure to update the function call in both the above mentioned places
  // to ensure that the same query variables are generated from both places

  const selectedSubject = _.get(filters, "selectedSubject", "");
  const evaluationTypes = _.get(filters, "evaluationTypes", "ALL");
  const selectedAssessmentTools = _.get(filters, "selectedAssessmentTools", []);
  const selectedAssessmentTypes = _.get(filters, "selectedAssessmentTypes", []);
  const selectedTerms = _.get(filters, "selectedTerms", []);
  const searchText = _.get(filters, "searchText", "");
  const selectedCategories = _.get(filters, "selectedCategories", []);
  const selectedStudents = _.sortBy(_.get(filters, "selectedStudents", []));

  const academicTermIds =
    isCumulativePeriod &&
    cumulativeTotalCalculationMethod === "FROM_ASSIGNMENTS"
      ? associatedTerms
      : !_.isEmpty(selectedTerms)
      ? selectedTerms
      : [];

  const { assessmentTypes = [], groupTypes = [] } = getGroupOrAssessmentTypes({
    curriculumType,
    selectedAssessmentTypes,
  });

  const assessmentTypeOptions = getAssesmentTypeOptions({
    t: _.noop,
    evaluationTypes: evaluationTypes,
    curriculumType: curriculumType,
    curriculumProgramId: curriculumProgramId,
  });

  // Sending no filters if all assessmentTypes are selected
  const _assessmentTypes = _.isEqual(
    _.sortBy(_.map(assessmentTypeOptions, "key")),
    _.sortBy(assessmentTypes)
  )
    ? []
    : assessmentTypes;

  const assessmentToolTypes = getEvaluationTypeBasedAssessmentToolTypeFilter({
    selectedAssessmentTools,
  });

  const customOrderBy = [];
  if (shouldGroupByCategories) {
    customOrderBy.push({
      entityType: CATEGORY,
      orderBy: DISPLAY_SEQUENCE,
      orderByDirection: ASC,
    });
  }

  if (!shouldGroupByCategories || isAssessmentGradebookHeaderEnhancements) {
    const assignmentOrderBy = getAssignmentOrderBy(displaySettings);
    customOrderBy.push(assignmentOrderBy);
  }

  const {
    showFinalCalculationExcludedTask,
    showOtherClassesTasks,
  } = getOthersDisplaySettings(displaySettings);

  let isExcludedFromFinalGradeCalculation;
  if (isAssessmentGradebookHeaderEnhancements) {
    isExcludedFromFinalGradeCalculation = showFinalCalculationExcludedTask
      ? undefined
      : false;
  } else {
    isExcludedFromFinalGradeCalculation = isHideTasksExcludedFromTermTotal
      ? false
      : undefined;
  }

  const isUsingNewGradingSetupInCourses = getIsConfigurationEnabledForCPAY({
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
  });
  const academicSetupType = getAcademicSetupTypeIfLearningCoursesEnabled();
  const isCourseBasedGrading = academicSetupType === COURSE_BASED_GRADING;

  const showTasksFromOtherClasses =
    (showSameSubjectGradeTasksInGradebook || isUsingNewGradingSetupInCourses) &&
    (isAssessmentGradebookHeaderEnhancements
      ? showOtherClassesTasks
      : !isHideTasksFromOtherClasses);

  const categoryKey = isUsingNewGradingSetupInCourses
    ? "globalCategoryIds"
    : "categoryIds";

  const assignmentFeedFilters = {
    courseIds: showTasksFromOtherClasses ? undefined : courseId,
    states: [ARCHIVED, PUBLISHED],
    [categoryKey]: selectedCategories,
    searchText: searchText,
    academicTermIds,
    assessmentTypes: _assessmentTypes,
    excludedAssessmentTypes:
      curriculumType === CURRICULUM_TYPE_DP ? EXCLUDED_ASSESSMENT_TYPES : [],
    groupTypes: groupTypes,
    isTeacherEvaluationEnabled: true,
    contentTypes: "ASSESSMENT",
    assessmentToolTypes,
    includedStudentIds: selectedStudents,
    isExcludedFromFinalGradeCalculation,
  };

  if (isUsingNewGradingSetupInCourses && isCourseBasedGrading) {
    assignmentFeedFilters.learningCourseIds = learningCourseId;
  } else {
    assignmentFeedFilters.subjectIds = !_.isEmpty(selectedSubject)
      ? _.split(selectedSubject, ",")
      : [];

    assignmentFeedFilters.primaryGradeId = showTasksFromOtherClasses
      ? primaryGradeId
      : undefined;
  }

  return {
    organizationId,
    first: 10,
    assessmentToolsFilters: { assessmentToolType: assessmentToolTypes },
    assignmentFeedFilters,
    customOrderBy,
    aclControlParametersFilters: {
      controlParameters: [MANAGE_GRADEBOOK_PERMISSION],
    },
  };
};

export const getOrganizationPointSetFromCache = ({ variables }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getOrganizationPointSetQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  return _.get(queryData, "node", {});
};

export const readAcademicCriteriaValueFragment = ({ id }) => {
  let fragmentData = {};

  try {
    fragmentData = client.readFragment({
      id: `AcademicCriteriaValue:${id}`,
      fragment: academicCriteriaValueFragment.academicCriteriaValue,
      fragmentName: "academicCriteriaValueItem",
    });
  } catch (e) {
    return {};
  }

  return fragmentData;
};

export const readCategoryItemFragment = ({ id, variables = {} }) => {
  try {
    const queryData = client.readFragment({
      id: `PointAssessmentSetCategory:${id}`,
      fragment: categoriesItemFragment.categoryItem,
      fragmentName: "categoryItem",
      variables,
    });

    return queryData;
  } catch (e) {
    return {};
  }
};

export const writeCategoryItemFragment = ({ id, variables = {}, data }) => {
  try {
    client.writeFragment({
      id: `PointAssessmentSetCategory:${id}`,
      fragment: categoriesItemFragment.categoryItem,
      fragmentName: "categoryItem",
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
};

export const readEvaluatedButNotSharedAssignmentsCountByTermFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getEvaluatedButNotSharedAssignmentsCountByTermQuery,
      variables,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const writeEvaluatedButNotSharedAssignmentsCountByTermInCache = ({
  variables,
  data,
}) => {
  try {
    client.writeQuery({
      query: getEvaluatedButNotSharedAssignmentsCountByTermQuery,
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
};

export const getEvaluatedButNotSharedAssignmentsFilter = ({
  courseId,
  selectedSubject,
  curriculumType,
  primaryGradeId,
  studentIds = [],
  showOtherClassesTasks,
  isHideTasksFromOtherClasses,
}) => {
  const showTasksFromOtherClasses =
    getIsSubjectGradebookEnabled() &&
    (ACLStore.can("FeatureFlag:AssessmentGradebookHeaderEnhancements")
      ? showOtherClassesTasks
      : !isHideTasksFromOtherClasses);

  return {
    courseIds: showTasksFromOtherClasses ? undefined : courseId,
    subjectIds: !_.isEmpty(selectedSubject)
      ? _.split(selectedSubject, ",")
      : [],
    states: [ARCHIVED, PUBLISHED],
    excludedAssessmentTypes:
      curriculumType === CURRICULUM_TYPE_DP ? EXCLUDED_ASSESSMENT_TYPES : [],
    isTeacherEvaluationEnabled: true,
    contentTypes: "ASSESSMENT",
    assessmentToolTypes: [SCORE],
    includedStudentIds: showTasksFromOtherClasses
      ? _.castArray(studentIds)
      : undefined,
    primaryGradeId: showTasksFromOtherClasses ? primaryGradeId : undefined,
    isExcludedFromFinalGradeCalculation: false,
    isEvaluatedButNotShared: true,
  };
};

// * updates element ratings in cache for a student assignment
export const updateStudentAssignmentIbPYPRatingsInCache = ({
  studentAssignmentId,
  ratings = [],
}) => {
  cache.modify({
    id: `StudentAssignment:${studentAssignmentId}`,
    fields: {
      ibPYPElementRatings: () => ratings,
    },
  });
};

// * updates element remarks in cache for a student assignment
export const updateStudentAssignmentRemarksInCache = ({
  studentAssignmentId,
  remarks = [],
}) => {
  cache.modify({
    id: `StudentAssignment:${studentAssignmentId}`,
    fields: {
      elementRemarks: () => remarks,
    },
  });
};

// * read students element ratings from cache
export const getStudentsElementRatingsFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getStudentsElementRatingsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "multiNode", []);
  else return [];
};

// * write students element ratings from cache
export const writeStudentsElementRatingsToCache = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getStudentsElementRatingsQuery,
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
};

export const getCourseGradesFromCacheV2 = ({
  courseId,
  selectedAcademicYearId,
  gradebookSelectedTermAcademicYearId,
}) => {
  if (selectedAcademicYearId === gradebookSelectedTermAcademicYearId) {
    const courseDetails = getCourseDetailsFromCache({
      id: courseId,
      courseGradeFilters: {
        academicYearId: selectedAcademicYearId,
      },
    });

    return {
      id: _.get(courseDetails, "id"),
      grades: _.get(courseDetails, "grades", []),
      primaryGrade: _.get(courseDetails, "primaryGrade", {}),
    };
  }

  const gradesData = getCourseGradesFromCache({
    id: courseId,
    courseGradeFilters: {
      academicYearId: gradebookSelectedTermAcademicYearId,
    },
  });

  return gradesData;
};

// * read student wise element remarks from cache
export const getStudentElementRemarksFromCache = ({ variables }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getStudentElementRemarksQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  return _.get(queryData, "node", {});
};

// * write student wise element remarks to cache
export const writeStudentElementRemarksToCache = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getStudentElementRemarksQuery,
      variables,
      data: { node: data },
    });
  } catch (e) {
    return {};
  }
};

export const getStudentRatingsFromCache = ({ studentId, variables }) => {
  let fragmentData = {};
  try {
    fragmentData = client.readFragment({
      id: `Student:${studentId}`,
      fragment: studentRatingsFragment.studentRatings,
      fragmentName: "StudentRatingsItem",
      variables,
    });
  } catch (e) {
    return {};
  }

  if (fragmentData != null) {
    return _.get(fragmentData, "ibPYPElementRatings", []);
  }

  return {};
};

export const writeStudentRatingsToCache = ({ studentId, variables, data }) => {
  try {
    client.writeFragment({
      id: `Student:${studentId}`,
      fragment: studentRatingsFragment.studentRatings,
      fragmentName: "StudentRatingsItem",
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
};

// * read gradebook assignments list from cache
export const getGradebookAssignmentsFromCache = ({ variables }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getGradebookAssignmentsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  return _.get(queryData, "node", {});
};

// * write gradebook assignments list to cache
export const writeGradebookAssignmentsToCache = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getGradebookAssignmentsQuery,
      variables,
      data: { node: data },
    });
  } catch (e) {
    return {};
  }
};

export const readAcademicCriteriaSetFragment = ({ id }) => {
  let fragmentData = {};

  try {
    fragmentData = client.readFragment({
      id: `AcademicCriteriaSet:${id}`,
      fragment: academicCriteriaSetFragment.academicCriteriaSet,
      fragmentName: "academicCriteriaSetItem",
    });
  } catch (e) {
    return {};
  }

  return fragmentData;
};

// * This is a placeholder for common data load in Gradebook GraphQL queries. If there is any common data that needs to be loaded for multiple queries in Gradebook, it can be added here.
export const useGradebookCommonDataLoad = params => {
  const {
    courseId,
    selectedAcademicYearId,
    gradebookSelectedTermAcademicYearId,
  } = params;

  useFetchQuery({
    query: getCourseGradesQuery,
    skipCondition:
      !courseId ||
      !gradebookSelectedTermAcademicYearId ||
      selectedAcademicYearId === gradebookSelectedTermAcademicYearId,
    variables: {
      id: courseId,
      courseGradeFilters: {
        academicYearId: gradebookSelectedTermAcademicYearId,
      },
    },
  });

  const courseGradesData = getCourseGradesFromCacheV2({
    courseId,
    selectedAcademicYearId,
    gradebookSelectedTermAcademicYearId,
  });

  const coursePrimaryGradeId = _.get(courseGradesData, "primaryGrade.id");

  return {
    gradesData: courseGradesData,
    coursePrimaryGradeId,
  };
};
